import _objectSpread from '@babel/runtime/helpers/esm/objectSpread2';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import React, { createElement, Fragment } from 'react';
import { object, keys, hasOwnProperty, isString, isPlainObject, isArray, invariant, ErrorConstant, isEmptyArray } from '@remirror/core';
import _classPrivateFieldSet from '@babel/runtime/helpers/esm/classPrivateFieldSet';
import _classPrivateFieldGet from '@babel/runtime/helpers/esm/classPrivateFieldGet';
import _extends from '@babel/runtime/helpers/esm/extends';
import _createForOfIteratorHelper from '@babel/runtime/helpers/esm/createForOfIteratorHelper';

/**
 * Used to map html attributes to their supported react names
 * Taken from https://github.com/facebook/react/blob/b87aabdfe1b7461e7331abb3601d9e6bb27544bc/packages/react-dom/src/shared/possibleStandardNames.js#L42
 */
var possibleStandardNames = {
  // HTML
  accept: 'accept',
  acceptcharset: 'acceptCharset',
  'accept-charset': 'acceptCharset',
  accesskey: 'accessKey',
  action: 'action',
  allowfullscreen: 'allowFullScreen',
  alt: 'alt',
  as: 'as',
  async: 'async',
  autocapitalize: 'autoCapitalize',
  autocomplete: 'autoComplete',
  autocorrect: 'autoCorrect',
  autofocus: 'autoFocus',
  autoplay: 'autoPlay',
  autosave: 'autoSave',
  capture: 'capture',
  cellpadding: 'cellPadding',
  cellspacing: 'cellSpacing',
  challenge: 'challenge',
  charset: 'charSet',
  checked: 'checked',
  children: 'children',
  cite: 'cite',
  class: 'className',
  classid: 'classID',
  classname: 'className',
  cols: 'cols',
  colspan: 'colSpan',
  content: 'content',
  contenteditable: 'contentEditable',
  contextmenu: 'contextMenu',
  controls: 'controls',
  controlslist: 'controlsList',
  coords: 'coords',
  crossorigin: 'crossOrigin',
  dangerouslysetinnerhtml: 'dangerouslySetInnerHTML',
  data: 'data',
  datetime: 'dateTime',
  default: 'default',
  defaultchecked: 'defaultChecked',
  defaultvalue: 'defaultValue',
  defer: 'defer',
  dir: 'dir',
  disabled: 'disabled',
  download: 'download',
  draggable: 'draggable',
  enctype: 'encType',
  for: 'htmlFor',
  form: 'form',
  formmethod: 'formMethod',
  formaction: 'formAction',
  formenctype: 'formEncType',
  formnovalidate: 'formNoValidate',
  formtarget: 'formTarget',
  frameborder: 'frameBorder',
  headers: 'headers',
  height: 'height',
  hidden: 'hidden',
  high: 'high',
  href: 'href',
  hreflang: 'hrefLang',
  htmlfor: 'htmlFor',
  httpequiv: 'httpEquiv',
  'http-equiv': 'httpEquiv',
  icon: 'icon',
  id: 'id',
  innerhtml: 'innerHTML',
  inputmode: 'inputMode',
  integrity: 'integrity',
  is: 'is',
  itemid: 'itemID',
  itemprop: 'itemProp',
  itemref: 'itemRef',
  itemscope: 'itemScope',
  itemtype: 'itemType',
  keyparams: 'keyParameter',
  keytype: 'keyType',
  kind: 'kind',
  label: 'label',
  lang: 'lang',
  list: 'list',
  loop: 'loop',
  low: 'low',
  manifest: 'manifest',
  marginwidth: 'marginWidth',
  marginheight: 'marginHeight',
  max: 'max',
  maxlength: 'maxLength',
  media: 'media',
  mediagroup: 'mediaGroup',
  method: 'method',
  min: 'min',
  minlength: 'minLength',
  multiple: 'multiple',
  muted: 'muted',
  name: 'name',
  nomodule: 'noModule',
  nonce: 'nonce',
  novalidate: 'noValidate',
  open: 'open',
  optimum: 'optimum',
  pattern: 'pattern',
  placeholder: 'placeholder',
  playsinline: 'playsInline',
  poster: 'poster',
  preload: 'preload',
  profile: 'profile',
  radiogroup: 'radioGroup',
  readonly: 'readOnly',
  referrerpolicy: 'referrerPolicy',
  rel: 'rel',
  required: 'required',
  reversed: 'reversed',
  role: 'role',
  rows: 'rows',
  rowspan: 'rowSpan',
  sandbox: 'sandbox',
  scope: 'scope',
  scoped: 'scoped',
  scrolling: 'scrolling',
  seamless: 'seamless',
  selected: 'selected',
  shape: 'shape',
  size: 'size',
  sizes: 'sizes',
  span: 'span',
  spellcheck: 'spellCheck',
  src: 'src',
  srcdoc: 'srcDoc',
  srclang: 'srcLang',
  srcset: 'srcSet',
  start: 'start',
  step: 'step',
  style: 'style',
  summary: 'summary',
  tabindex: 'tabIndex',
  target: 'target',
  title: 'title',
  type: 'type',
  usemap: 'useMap',
  value: 'value',
  width: 'width',
  wmode: 'wmode',
  wrap: 'wrap',
  // SVG
  about: 'about',
  accentheight: 'accentHeight',
  'accent-height': 'accentHeight',
  accumulate: 'accumulate',
  additive: 'additive',
  alignmentbaseline: 'alignmentBaseline',
  'alignment-baseline': 'alignmentBaseline',
  allowreorder: 'allowReorder',
  alphabetic: 'alphabetic',
  amplitude: 'amplitude',
  arabicform: 'arabicForm',
  'arabic-form': 'arabicForm',
  ascent: 'ascent',
  attributename: 'attributeName',
  attributetype: 'attributeType',
  autoreverse: 'autoReverse',
  azimuth: 'azimuth',
  basefrequency: 'baseFrequency',
  baselineshift: 'baselineShift',
  'baseline-shift': 'baselineShift',
  baseprofile: 'baseProfile',
  bbox: 'bbox',
  begin: 'begin',
  bias: 'bias',
  by: 'by',
  calcmode: 'calcMode',
  capheight: 'capHeight',
  'cap-height': 'capHeight',
  clip: 'clip',
  clippath: 'clipPath',
  'clip-path': 'clipPath',
  clippathunits: 'clipPathUnits',
  cliprule: 'clipRule',
  'clip-rule': 'clipRule',
  color: 'color',
  colorinterpolation: 'colorInterpolation',
  'color-interpolation': 'colorInterpolation',
  colorinterpolationfilters: 'colorInterpolationFilters',
  'color-interpolation-filters': 'colorInterpolationFilters',
  colorprofile: 'colorProfile',
  'color-profile': 'colorProfile',
  colorrendering: 'colorRendering',
  'color-rendering': 'colorRendering',
  contentscripttype: 'contentScriptType',
  contentstyletype: 'contentStyleType',
  cursor: 'cursor',
  cx: 'cx',
  cy: 'cy',
  d: 'd',
  datatype: 'datatype',
  decelerate: 'decelerate',
  descent: 'descent',
  diffuseconstant: 'diffuseConstant',
  direction: 'direction',
  display: 'display',
  divisor: 'divisor',
  dominantbaseline: 'dominantBaseline',
  'dominant-baseline': 'dominantBaseline',
  dur: 'dur',
  dx: 'dx',
  dy: 'dy',
  edgemode: 'edgeMode',
  elevation: 'elevation',
  enablebackground: 'enableBackground',
  'enable-background': 'enableBackground',
  end: 'end',
  exponent: 'exponent',
  externalresourcesrequired: 'externalResourcesRequired',
  fill: 'fill',
  fillopacity: 'fillOpacity',
  'fill-opacity': 'fillOpacity',
  fillrule: 'fillRule',
  'fill-rule': 'fillRule',
  filter: 'filter',
  filterres: 'filterRes',
  filterunits: 'filterUnits',
  floodopacity: 'floodOpacity',
  'flood-opacity': 'floodOpacity',
  floodcolor: 'floodColor',
  'flood-color': 'floodColor',
  focusable: 'focusable',
  fontfamily: 'fontFamily',
  'font-family': 'fontFamily',
  fontsize: 'fontSize',
  'font-size': 'fontSize',
  fontsizeadjust: 'fontSizeAdjust',
  'font-size-adjust': 'fontSizeAdjust',
  fontstretch: 'fontStretch',
  'font-stretch': 'fontStretch',
  fontstyle: 'fontStyle',
  'font-style': 'fontStyle',
  fontvariant: 'fontVariant',
  'font-variant': 'fontVariant',
  fontweight: 'fontWeight',
  'font-weight': 'fontWeight',
  format: 'format',
  from: 'from',
  fx: 'fx',
  fy: 'fy',
  g1: 'g1',
  g2: 'g2',
  glyphname: 'glyphName',
  'glyph-name': 'glyphName',
  glyphorientationhorizontal: 'glyphOrientationHorizontal',
  'glyph-orientation-horizontal': 'glyphOrientationHorizontal',
  glyphorientationvertical: 'glyphOrientationVertical',
  'glyph-orientation-vertical': 'glyphOrientationVertical',
  glyphref: 'glyphRef',
  gradienttransform: 'gradientTransform',
  gradientunits: 'gradientUnits',
  hanging: 'hanging',
  horizadvx: 'horizAdvX',
  'horiz-adv-x': 'horizAdvX',
  horizoriginx: 'horizOriginX',
  'horiz-origin-x': 'horizOriginX',
  ideographic: 'ideographic',
  imagerendering: 'imageRendering',
  'image-rendering': 'imageRendering',
  in2: 'in2',
  in: 'in',
  inlist: 'inlist',
  intercept: 'intercept',
  k1: 'k1',
  k2: 'k2',
  k3: 'k3',
  k4: 'k4',
  k: 'k',
  kernelmatrix: 'kernelMatrix',
  kernelunitlength: 'kernelUnitLength',
  kerning: 'kerning',
  keypoints: 'keyPoints',
  keysplines: 'keySplines',
  keytimes: 'keyTimes',
  lengthadjust: 'lengthAdjust',
  letterspacing: 'letterSpacing',
  'letter-spacing': 'letterSpacing',
  lightingcolor: 'lightingColor',
  'lighting-color': 'lightingColor',
  limitingconeangle: 'limitingConeAngle',
  local: 'local',
  markerend: 'markerEnd',
  'marker-end': 'markerEnd',
  markerheight: 'markerHeight',
  markermid: 'markerMid',
  'marker-mid': 'markerMid',
  markerstart: 'markerStart',
  'marker-start': 'markerStart',
  markerunits: 'markerUnits',
  markerwidth: 'markerWidth',
  mask: 'mask',
  maskcontentunits: 'maskContentUnits',
  maskunits: 'maskUnits',
  mathematical: 'mathematical',
  mode: 'mode',
  numoctaves: 'numOctaves',
  offset: 'offset',
  opacity: 'opacity',
  operator: 'operator',
  order: 'order',
  orient: 'orient',
  orientation: 'orientation',
  origin: 'origin',
  overflow: 'overflow',
  overlineposition: 'overlinePosition',
  'overline-position': 'overlinePosition',
  overlinethickness: 'overlineThickness',
  'overline-thickness': 'overlineThickness',
  paintorder: 'paintOrder',
  'paint-order': 'paintOrder',
  panose1: 'panose1',
  'panose-1': 'panose1',
  pathlength: 'pathLength',
  patterncontentunits: 'patternContentUnits',
  patterntransform: 'patternTransform',
  patternunits: 'patternUnits',
  pointerevents: 'pointerEvents',
  'pointer-events': 'pointerEvents',
  points: 'points',
  pointsatx: 'pointsAtX',
  pointsaty: 'pointsAtY',
  pointsatz: 'pointsAtZ',
  prefix: 'prefix',
  preservealpha: 'preserveAlpha',
  preserveaspectratio: 'preserveAspectRatio',
  primitiveunits: 'primitiveUnits',
  property: 'property',
  r: 'r',
  radius: 'radius',
  refx: 'refX',
  refy: 'refY',
  renderingintent: 'renderingIntent',
  'rendering-intent': 'renderingIntent',
  repeatcount: 'repeatCount',
  repeatdur: 'repeatDur',
  requiredextensions: 'requiredExtensions',
  requiredfeatures: 'requiredFeatures',
  resource: 'resource',
  restart: 'restart',
  result: 'result',
  results: 'results',
  rotate: 'rotate',
  rx: 'rx',
  ry: 'ry',
  scale: 'scale',
  security: 'security',
  seed: 'seed',
  shaperendering: 'shapeRendering',
  'shape-rendering': 'shapeRendering',
  slope: 'slope',
  spacing: 'spacing',
  specularconstant: 'specularConstant',
  specularexponent: 'specularExponent',
  speed: 'speed',
  spreadmethod: 'spreadMethod',
  startoffset: 'startOffset',
  stddeviation: 'stdDeviation',
  stemh: 'stemh',
  stemv: 'stemv',
  stitchtiles: 'stitchTiles',
  stopcolor: 'stopColor',
  'stop-color': 'stopColor',
  stopopacity: 'stopOpacity',
  'stop-opacity': 'stopOpacity',
  strikethroughposition: 'strikethroughPosition',
  'strikethrough-position': 'strikethroughPosition',
  strikethroughthickness: 'strikethroughThickness',
  'strikethrough-thickness': 'strikethroughThickness',
  string: 'string',
  stroke: 'stroke',
  strokedasharray: 'strokeDasharray',
  'stroke-dasharray': 'strokeDasharray',
  strokedashoffset: 'strokeDashoffset',
  'stroke-dashoffset': 'strokeDashoffset',
  strokelinecap: 'strokeLinecap',
  'stroke-linecap': 'strokeLinecap',
  strokelinejoin: 'strokeLinejoin',
  'stroke-linejoin': 'strokeLinejoin',
  strokemiterlimit: 'strokeMiterlimit',
  'stroke-miterlimit': 'strokeMiterlimit',
  strokewidth: 'strokeWidth',
  'stroke-width': 'strokeWidth',
  strokeopacity: 'strokeOpacity',
  'stroke-opacity': 'strokeOpacity',
  suppresscontenteditablewarning: 'suppressContentEditableWarning',
  suppresshydrationwarning: 'suppressHydrationWarning',
  surfacescale: 'surfaceScale',
  systemlanguage: 'systemLanguage',
  tablevalues: 'tableValues',
  targetx: 'targetX',
  targety: 'targetY',
  textanchor: 'textAnchor',
  'text-anchor': 'textAnchor',
  textdecoration: 'textDecoration',
  'text-decoration': 'textDecoration',
  textlength: 'textLength',
  textrendering: 'textRendering',
  'text-rendering': 'textRendering',
  to: 'to',
  transform: 'transform',
  typeof: 'typeof',
  u1: 'u1',
  u2: 'u2',
  underlineposition: 'underlinePosition',
  'underline-position': 'underlinePosition',
  underlinethickness: 'underlineThickness',
  'underline-thickness': 'underlineThickness',
  unicode: 'unicode',
  unicodebidi: 'unicodeBidi',
  'unicode-bidi': 'unicodeBidi',
  unicoderange: 'unicodeRange',
  'unicode-range': 'unicodeRange',
  unitsperem: 'unitsPerEm',
  'units-per-em': 'unitsPerEm',
  unselectable: 'unselectable',
  valphabetic: 'vAlphabetic',
  'v-alphabetic': 'vAlphabetic',
  values: 'values',
  vectoreffect: 'vectorEffect',
  'vector-effect': 'vectorEffect',
  version: 'version',
  vertadvy: 'vertAdvY',
  'vert-adv-y': 'vertAdvY',
  vertoriginx: 'vertOriginX',
  'vert-origin-x': 'vertOriginX',
  vertoriginy: 'vertOriginY',
  'vert-origin-y': 'vertOriginY',
  vhanging: 'vHanging',
  'v-hanging': 'vHanging',
  videographic: 'vIdeographic',
  'v-ideographic': 'vIdeographic',
  viewbox: 'viewBox',
  viewtarget: 'viewTarget',
  visibility: 'visibility',
  vmathematical: 'vMathematical',
  'v-mathematical': 'vMathematical',
  vocab: 'vocab',
  widths: 'widths',
  wordspacing: 'wordSpacing',
  'word-spacing': 'wordSpacing',
  writingmode: 'writingMode',
  'writing-mode': 'writingMode',
  x1: 'x1',
  x2: 'x2',
  x: 'x',
  xchannelselector: 'xChannelSelector',
  xheight: 'xHeight',
  'x-height': 'xHeight',
  xlinkactuate: 'xlinkActuate',
  'xlink:actuate': 'xlinkActuate',
  xlinkarcrole: 'xlinkArcrole',
  'xlink:arcrole': 'xlinkArcrole',
  xlinkhref: 'xlinkHref',
  'xlink:href': 'xlinkHref',
  xlinkrole: 'xlinkRole',
  'xlink:role': 'xlinkRole',
  xlinkshow: 'xlinkShow',
  'xlink:show': 'xlinkShow',
  xlinktitle: 'xlinkTitle',
  'xlink:title': 'xlinkTitle',
  xlinktype: 'xlinkType',
  'xlink:type': 'xlinkType',
  xmlbase: 'xmlBase',
  'xml:base': 'xmlBase',
  xmllang: 'xmlLang',
  'xml:lang': 'xmlLang',
  xmlns: 'xmlns',
  'xml:space': 'xmlSpace',
  xmlnsxlink: 'xmlnsXlink',
  'xmlns:xlink': 'xmlnsXlink',
  xmlspace: 'xmlSpace',
  y1: 'y1',
  y2: 'y2',
  y: 'y',
  ychannelselector: 'yChannelSelector',
  z: 'z',
  zoomandpan: 'zoomAndPan'
};

function getPossibleStandardName(key) {
  if (!hasOwnProperty(possibleStandardNames, key)) {
    return key;
  }

  return possibleStandardNames[key] || key;
}
/**
 * Map standard html attribute names to their react equivalents.
 */


function mapProps(props) {
  var transformedProps = object();

  var _iterator = _createForOfIteratorHelper(keys(props)),
      _step;

  try {
    for (_iterator.s(); !(_step = _iterator.n()).done;) {
      var key = _step.value;
      var name = getPossibleStandardName(key);
      transformedProps[name] = props[key];

      if (name === 'contentEditable') {
        transformedProps.suppressContentEditableWarning = true;
      }
    }
  } catch (err) {
    _iterator.e(err);
  } finally {
    _iterator.f();
  }

  return transformedProps;
}

/**
 * Gather up all the toDOM methods from the provided spec object
 *
 * @param specs - the prosemirror schema specs for each node / mark
 */
function gatherDomMethods(specs) {
  var result = object();

  for (var name in specs) {
    if (!hasOwnProperty(specs, name)) {
      continue;
    }

    var toDOM = specs[name].toDOM;

    if (toDOM) {
      result[name] = toDOM;
    }
  }

  return result;
}

var _components = /*#__PURE__*/new WeakMap();

var _view = /*#__PURE__*/new WeakMap();

/**
 * Serialize the extension provided schema into a JSX element that can be displayed node and non-dom environments.
 */
class ReactSerializer {
  /**
   * Receives the return value from toDOM defined in the node schema and transforms it
   * into JSX
   *
   * @param structure - The DOMOutput spec for the current node
   * @param wraps - passed through any elements that this component should be parent of
   */
  static renderSpec(structure, wraps) {
    if (isString(structure)) {
      return structure;
    }

    var Component = structure[0];
    var props = object();
    var attributes = structure[1];
    var children = [];
    var currentIndex = 1;

    if (isPlainObject(attributes) && !isArray(attributes)) {
      currentIndex = 2;

      for (var name in attributes) {
        if (attributes[name] != null) {
          props[name] = attributes[name];
        }
      }
    }

    for (var ii = currentIndex; ii < structure.length; ii++) {
      var child = structure[ii];

      if (child === 0) {
        !!(ii < structure.length - 1 || ii > currentIndex) ? process.env.NODE_ENV !== "production" ? invariant(false, {
          message: 'Content hole (0) must be the only child of its parent node',
          code: ErrorConstant.INTERNAL
        }) : invariant(false) : void 0;
        return /*#__PURE__*/createElement(Component, mapProps(props), wraps);
      }

      children.push(ReactSerializer.renderSpec(child, wraps));
    }

    return /*#__PURE__*/createElement(Component, mapProps(props), ...children);
  }
  /**
   * Create a serializer from the extension manager
   *
   * @param manager
   */


  static fromManager(manager) {
    return new ReactSerializer(this.nodesFromManager(manager), this.marksFromManager(manager), manager);
  }
  /**
   * Pluck nodes from the extension manager
   *
   * @param manager
   */


  static nodesFromManager(manager) {
    var result = gatherDomMethods(manager.nodes);

    if (!result.text) {
      result.text = node => node.text ? node.text : '';
    }

    return result;
  }
  /**
   * Pluck marks from the extension manager
   *
   * @param manager
   */


  static marksFromManager(manager) {
    return gatherDomMethods(manager.marks);
  }

  constructor(nodes, marks, manager) {
    var _manager$store$compon;

    _components.set(this, {
      writable: true,
      value: void 0
    });

    _view.set(this, {
      writable: true,
      value: void 0
    });

    this.nodes = nodes;
    this.marks = marks;

    _classPrivateFieldSet(this, _components, (_manager$store$compon = manager.store.components) !== null && _manager$store$compon !== void 0 ? _manager$store$compon : object());

    _classPrivateFieldSet(this, _view, manager.view);
  }
  /**
   * The main entry method on this class for traversing through a schema tree and creating JSx.
   *
   * ```ts
   * reactSerializer.serializeFragment(fragment)
   * ```
   *
   * @param fragment
   */


  serializeFragment(fragment) {
    var children = [];
    fragment.forEach(node => {
      var child;
      child = this.serializeNode(node);
      node.marks.reverse().forEach(mark => {
        // TODO test behaviour expectations for `spanning` marks. Currently not HANDLED.
        child = this.serializeMark(mark, node.isInline, child);
      });
      children.push(child);
    });
    return /*#__PURE__*/createElement(Fragment, {}, ...children);
  }
  /**
   * Transform the passed in node into a JSX Element
   *
   * @param node
   */


  serializeNode(node) {
    var managerStoreComponent = _classPrivateFieldGet(this, _components)[node.type.name];

    var toDOM = this.nodes[node.type.name];
    var children;

    if (node.content.childCount > 0) {
      children = this.serializeFragment(node.content);
    }

    if (managerStoreComponent) {
      var Component = managerStoreComponent.Component,
          props = managerStoreComponent.props;
      return /*#__PURE__*/React.createElement(Component, _extends({}, props, {
        node: node,
        view: _classPrivateFieldGet(this, _view)
      }), children);
    }

    return toDOM && ReactSerializer.renderSpec(toDOM(node), children);
  }
  /**
   * Transform the provided mark into a JSX Element that wraps the current node
   *
   * @param mark
   * @param inline
   * @param wrappedElement
   */


  serializeMark(mark, inline, wrappedElement) {
    var toDOM = this.marks[mark.type.name]; // TODO add support for mark components if requested by community

    return toDOM && ReactSerializer.renderSpec(toDOM(mark, inline), wrappedElement);
  }

}

/* Inspired by https://github.com/rexxars/react-prosemirror-document */

var normalizeMark = mark => isString(mark) ? {
  type: mark,
  attrs: {}
} : _objectSpread({
  attrs: {}
}, mark);

var TextHandler = (_ref) => {
  var node = _ref.node,
      props = _objectWithoutProperties(_ref, ["node"]);

  if (!node.text) {
    return null;
  }

  var textElement = /*#__PURE__*/React.createElement(Fragment, null, node.text);

  if (!node.marks) {
    return textElement;
  }

  var _iterator = _createForOfIteratorHelper(node.marks),
      _step;

  try {
    for (_iterator.s(); !(_step = _iterator.n()).done;) {
      var mark = _step.value;
      var normalized = normalizeMark(mark);
      var MarkHandler = props.markMap[normalized.type];

      if (!MarkHandler) {
        if (!props.skipUnknownMarks) {
          throw new Error("No handler for mark type `".concat(normalized.type, "` registered"));
        }

        continue;
      }

      textElement = /*#__PURE__*/React.createElement(MarkHandler, normalized.attrs, textElement);
    }
  } catch (err) {
    _iterator.e(err);
  } finally {
    _iterator.f();
  }

  return textElement;
};

var CodeBlock = props => {
  var content = props.node.content;

  if (!content) {
    return null;
  }

  content.map((node, ii) => {
    return /*#__PURE__*/React.createElement(TextHandler, _extends({
      key: ii
    }, _objectSpread(_objectSpread({}, props), {}, {
      node
    })));
  });
  return /*#__PURE__*/React.createElement("pre", null, /*#__PURE__*/React.createElement("code", null, content));
};

var Doc = (_ref2) => {
  var _node$attrs;

  var node = _ref2.node,
      props = _objectWithoutProperties(_ref2, ["node"]);

  var content = node.content;

  if (!content || isEmptyArray(content)) {
    return null;
  }

  var children = content.map((child, ii) => {
    return /*#__PURE__*/React.createElement(RenderTree, _extends({
      json: child
    }, props, {
      key: ii
    }));
  });
  return /*#__PURE__*/React.createElement("div", (_node$attrs = node.attrs) !== null && _node$attrs !== void 0 ? _node$attrs : object(), children);
};

var defaultTypeMap = {
  doc: Doc,
  paragraph: 'p',
  image: 'img',
  hardBreak: 'br',
  codeBlock: CodeBlock,
  text: TextHandler
};
var defaultMarkMap = {
  italic: 'em',
  bold: 'strong',
  code: 'code',
  link: 'a',
  underline: 'u'
};

/**
 * A recursively rendered tree.
 */
var RenderTree = props => {
  var _json$attrs;

  var json = props.json,
      _props$markMap = props.markMap,
      markMap = _props$markMap === void 0 ? defaultMarkMap : _props$markMap,
      _props$skipUnknownMar = props.skipUnknownMarks,
      skipUnknownMarks = _props$skipUnknownMar === void 0 ? false : _props$skipUnknownMar,
      _props$skipUnknownTyp = props.skipUnknownTypes,
      skipUnknownTypes = _props$skipUnknownTyp === void 0 ? false : _props$skipUnknownTyp,
      _props$typeMap = props.typeMap,
      typeMap = _props$typeMap === void 0 ? defaultTypeMap : _props$typeMap;

  if (json.type === 'text' && json.text && (!json.marks || isEmptyArray(json.marks))) {
    return /*#__PURE__*/React.createElement(Fragment, null, json.text); // For some reason FunctionalComponent don't allow returning react-nodes
  }

  var rest = {
    markMap,
    typeMap,
    skipUnknownMarks,
    skipUnknownTypes
  };
  var TypeHandler = typeMap[json.type];

  if (!TypeHandler) {
    if (!skipUnknownTypes) {
      throw new Error("No handler for node type `".concat(json.type, "` registered"));
    }

    return null;
  }

  var childProps = isString(TypeHandler) ? (_json$attrs = json.attrs) !== null && _json$attrs !== void 0 ? _json$attrs : object() : _objectSpread(_objectSpread({}, rest), {}, {
    node: json
  });
  var content = json.content;

  if (!content || isEmptyArray(content)) {
    return /*#__PURE__*/React.createElement(TypeHandler, childProps);
  }

  var children = content.map((child, ii) => {
    return /*#__PURE__*/React.createElement(RenderTree, _extends({
      key: ii,
      json: child
    }, rest));
  });
  return /*#__PURE__*/React.createElement(TypeHandler, childProps, children);
};

export { ReactSerializer as R, RenderTree as a, gatherDomMethods as g, mapProps as m };
